/**
 * System Module Helpers
 *
 * Shared transformation functions for converting internal types to DTOs.
 * Used by both compute() functions and the controller.
 */

import { join } from 'node:path'
import { existsSync } from 'node:fs'
import type { Context } from 'hono'
import type {
  ModuleContext,
  ModuleManifest,
  PluginManifest,
  InputType,
  FieldDefinitionDTO,
  FieldCondition,
  ActionDefinitionDTO,
  ActionDefinition,
  ModuleDTO,
  PluginDTO,
  EntityDefinitionDTO,
  LocalizedString,
  WorkflowConfig,
  FieldAccessConfig
} from '@gzl10/nexus-sdk'

/**
 * Check if a module has a seed file
 */
export function moduleHasSeedWithPath(mod: ModuleManifest, libPath: string): boolean {
  if (mod.seed) return true
  const seedPath = join(libPath, 'dist', 'modules', mod.name, `${mod.name}.seed.js`)
  return existsSync(seedPath)
}

/**
 * Checks if value is a FieldCondition object (serializable condition)
 * Uses same operators as FilterOperators: $eq, $ne, $in, $nin, $isnull
 */
function isFieldCondition(value: unknown): value is FieldCondition {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  return typeof obj['field'] === 'string' && (
    '$eq' in obj ||
    '$ne' in obj ||
    '$in' in obj ||
    '$nin' in obj ||
    '$isnull' in obj
  )
}

/**
 * Evaluates a ConditionalBoolean to a serializable value.
 * - FieldCondition objects are passed through (serializable)
 * - Functions are evaluated with empty data to get default value
 * - Booleans are passed through
 */
export function evalConditionalBoolean(value: unknown): boolean | FieldCondition | undefined {
  if (value === undefined) return undefined
  // Pass through FieldCondition objects for frontend evaluation
  if (isFieldCondition(value)) return value
  // Evaluate functions with empty data
  if (typeof value === 'function') return value({}) as boolean
  return value as boolean
}

/**
 * Converts FieldDefinition to a serializable DTO (no functions)
 * Filters out sensitive database schema information for security
 */
export function toFieldDTO(name: string, field: Record<string, unknown>): FieldDefinitionDTO {
  const relation = field['relation'] as Record<string, unknown> | undefined
  const validation = field['validation'] as Record<string, unknown> | undefined
  const options = field['options'] as Record<string, unknown> | undefined
  const storage = field['storage'] as Record<string, unknown> | undefined
  const meta = field['meta'] as Record<string, unknown> | undefined
  const inputProps = field['inputProps'] as Record<string, unknown> | undefined
  const displayProps = field['displayProps'] as Record<string, unknown> | undefined
  const access = field['access'] as FieldAccessConfig | undefined

  return {
    name,
    label: field['label'] as string,
    input: field['input'] as InputType,
    placeholder: field['placeholder'] as string | undefined,
    hint: field['hint'] as LocalizedString | undefined,
    hidden: evalConditionalBoolean(field['hidden']),
    disabled: evalConditionalBoolean(field['disabled']),
    required: evalConditionalBoolean(field['required']),
    // db: FILTERED - sensitive database schema information
    defaultValue: field['defaultValue'] ?? (field['db'] as Record<string, unknown> | undefined)?.['default'],
    localized: field['localized'] as boolean | undefined,
    relation: relation ? {
      labelField: relation['labelField'] as string | undefined
    } : undefined,
    validation: validation ? {
      min: validation['min'] as number | undefined,
      max: validation['max'] as number | undefined,
      pattern: validation['pattern'] as string | undefined,
      format: validation['format'] as string | undefined,
      enum: validation['enum'] as string[] | undefined
    } : undefined,
    options: options
      ? Array.isArray(options)
        // Direct array format from useSelectField: [{ value, label }]
        ? { static: options as unknown as Array<{ value: string; label: string }> }
        // Object format: { endpoint, valueField, labelField, static }
        : {
            endpoint: options['endpoint'] as string | undefined,
            valueField: options['valueField'] as string | undefined,
            labelField: options['labelField'] as string | undefined,
            static: options['static'] as Array<{ value: string; label: string }> | undefined
          }
      : undefined,
    storage: storage ? {
      accept: storage['accept'] as string | undefined,
      maxSize: storage['maxSize'] as number | undefined,
      maxFiles: storage['maxFiles'] as number | undefined,
      // folder: FILTERED - sensitive server path information
      thumbnails: storage['thumbnails'] as Array<{ width: number; height: number }> | undefined,
      dedupe: storage['dedupe'] as boolean | undefined,
      visibility: storage['visibility'] as 'public' | 'internal' | 'private' | undefined
    } : undefined,
    meta: meta ? {
      sortable: meta['sortable'] as boolean | undefined,
      searchable: meta['searchable'] as boolean | undefined,
      exportable: meta['exportable'] as boolean | undefined,
      showInDisplay: meta['showInDisplay'] as boolean | undefined,
      showInForm: meta['showInForm'] as boolean | 'create' | 'edit' | undefined
    } : undefined,
    inputProps,
    displayProps,
    access: access ? {
      ...(access.read ? { read: access.read } : {}),
      ...(access.create ? { create: access.create } : {}),
      ...(access.update ? { update: access.update } : {})
    } : undefined
  }
}

/**
 * Converts ActionDefinition to a serializable DTO (no functions).
 * When req/ctx are provided, evaluates disabled() with request context.
 * Without req/ctx, disabled is left undefined (client assumes enabled).
 */
export function toActionDTO(action: ActionDefinition, c?: Context<any>, ctx?: ModuleContext): ActionDefinitionDTO {
  const inputFields = action.input ?? {}
  const input: Record<string, FieldDefinitionDTO> = {}
  for (const [name, field] of Object.entries(inputFields)) {
    input[name] = toFieldDTO(name, field as unknown as Record<string, unknown>)
  }

  const outputFields = action.output ?? {}
  const output: Record<string, FieldDefinitionDTO> = {}
  for (const [name, field] of Object.entries(outputFields)) {
    output[name] = toFieldDTO(name, field as unknown as Record<string, unknown>)
  }

  // Evaluate disabled: function needs req/ctx, FieldCondition passed through as-is
  let isDisabled: boolean | FieldCondition | undefined
  if (typeof action.disabled === 'function') {
    isDisabled = c && ctx ? action.disabled(ctx, c) : undefined
  } else {
    isDisabled = action.disabled
  }

  return {
    key: action.key,
    label: action.label,
    icon: action.icon,
    scope: action.scope,
    group: action.group,
    disabled: isDisabled || undefined,
    disabledReason: isDisabled ? action.disabledReason : undefined,
    visible: action.visible,
    variant: action.variant,
    method: action.method,
    input: Object.keys(input).length > 0 ? input : undefined,
    order: action.order,
    batch: action.batch ? true : undefined,
    timeout: action.timeout,
    output: Object.keys(output).length > 0 ? output : (action.output ? {} : undefined),
    outputPage: action.outputPage,
    confirm: action.confirm,
    postAction: action.postAction
  }
}

/**
 * Infer route prefix for entity DTO.
 * Mirrors inferEntityRoutePath() from entity-factory.ts to ensure frontend
 * knows the actual mounted route, not just the explicit routePrefix.
 */
function inferRoutePrefix(def: Record<string, unknown>): string {
  // Explicit routePrefix
  if (def['routePrefix'] !== undefined && def['routePrefix'] !== null) {
    const rp = def['routePrefix'] as string
    return rp.startsWith('/') ? rp : `/${rp}`
  }

  // Infer from table: remote_hosts -> /hosts
  if (def['table']) {
    const table = def['table'] as string
    const parts = table.split('_')
    const name = parts.length > 1 ? parts.slice(1).join('_') : table
    return `/${name}`
  }

  // Infer from key (single entities): remote_settings -> /remote_settings
  if (def['key']) {
    return `/${def['key']}`
  }

  // Fallback from label
  const label = def['label']
  if (typeof label === 'string') return `/${label.toLowerCase().replace(/\s+/g, '-')}`
  if (label && typeof label === 'object') {
    const en = (label as Record<string, string>)['en'] ?? ''
    return `/${en.toLowerCase().replace(/\s+/g, '-')}`
  }
  return '/'
}

/**
 * Converts an entity definition to a serializable DTO
 */
export function toEntityDefinitionDTO(def: Record<string, unknown>, _engine: ModuleContext['engine'], moduleName?: string): EntityDefinitionDTO {
  const defFields = (def['fields'] ?? {}) as Record<string, unknown>
  const fields: Record<string, FieldDefinitionDTO> = {}
  for (const [name, field] of Object.entries(defFields)) {
    fields[name] = toFieldDTO(name, field as unknown as Record<string, unknown>)
  }

  // Pre-calculate field metadata for frontend
  const fieldEntries = Object.entries(defFields)
  const searchableFields = fieldEntries.filter(([, f]) => {
    const field = f as unknown as Record<string, unknown>
    const meta = field['meta'] as Record<string, unknown> | undefined
    return meta?.['searchable'] === true && !field['hidden']
  })
  const sortableFields = fieldEntries.filter(([, f]) => {
    const field = f as unknown as Record<string, unknown>
    const meta = field['meta'] as Record<string, unknown> | undefined
    return meta?.['sortable'] === true && !field['hidden']
  })

  // Groupable fields: explicit definition takes precedence over auto-detection
  const explicitGroupable = def['groupableFields'] as string[] | undefined
  const groupableInputTypes = ['select', 'switch', 'checkbox', 'radio', 'tags']
  const autoGroupableFields = fieldEntries.filter(([, f]) => {
    const field = f as unknown as Record<string, unknown>
    const inputType = field['input'] as string | undefined
    return groupableInputTypes.includes(inputType ?? '') && !field['hidden']
  })
  const resolvedGroupableFields = explicitGroupable
    ?? (autoGroupableFields.length > 0 ? autoGroupableFields.map(([name]) => name) : undefined)

  const entityType = (def['type'] as string) ?? 'collection'
  const explicitDisplayMode = def['displayMode'] as 'table' | 'list' | 'masonry' | 'tree' | undefined
  const explicitAvailableModes = def['availableDisplayModes'] as string[] | undefined
  const defaultDisplayMode = explicitDisplayMode
    ?? (explicitAvailableModes?.length === 1 ? explicitAvailableModes[0] as typeof explicitDisplayMode : undefined)
    ?? (['tree', 'dag'].includes(entityType) ? 'tree' : 'table')
  const entityIdent = (def['table'] as string | undefined) ?? (def['key'] as string | undefined)

  return {
    id: (def as unknown as { _id: string })._id,
    key: def['key'] as string | undefined,
    type: entityType as EntityDefinitionDTO['type'],
    label: def['label'] as EntityDefinitionDTO['label'],
    labelField: def['labelField'] as string | undefined,
    icon: def['icon'] as string | undefined,
    routePrefix: inferRoutePrefix(def),
    order: def['order'] as number | undefined,
    fields,
    fieldsCount: Object.keys(fields).length,
    hasTimestamps: !!def['timestamps'],
    hasAudit: !!def['audit'],
    caslSubject: (def['casl'] as Record<string, unknown> | undefined)?.['subject'] as string | undefined,
    isSingle: entityType === 'computed' && !!def['isSingle'],
    displayMode: explicitDisplayMode,
    defaultDisplayMode,
    availableDisplayModes: (() => {
      const explicit = def['availableDisplayModes'] as string[] | undefined
      if (explicit?.length) return explicit as EntityDefinitionDTO['availableDisplayModes']
      const modes: string[] = ['table', 'list', 'masonry']
      if (['tree', 'dag'].includes(entityType)) modes.push('tree')
      if ((resolvedGroupableFields?.length ?? 0) > 0) modes.push('board')
      if (def['calendarFrom']) modes.push('calendar')
      return modes as EntityDefinitionDTO['availableDisplayModes']
    })(),
    hasSearchableFields: searchableFields.length > 0,
    hasSortableFields: sortableFields.length > 0,
    sortableFieldOptions: sortableFields.map(([name, f]) => ({
      value: name,
      label: (f as unknown as Record<string, unknown>)['label'] as string
    })),
    groupableFields: resolvedGroupableFields,
    columnDragFields: def['columnDragFields'] as string[] | undefined,
    groupBy: def['groupBy'] as string | undefined,
    subgroupBy: def['subgroupBy'] as string | undefined,
    calendarFrom: def['calendarFrom'] as string | undefined,
    calendarTo: def['calendarTo'] as string | undefined,
    defaultSort: def['defaultSort'] as { field: string; order: 'asc' | 'desc' } | undefined,
    allowCreate: def['allowCreate'] !== undefined ? !!def['allowCreate'] : ['collection', 'external', 'tree', 'dag'].includes(entityType),
    allowEdit: def['allowEdit'] !== undefined ? !!def['allowEdit'] : ['collection', 'external', 'tree', 'dag', 'single', 'config'].includes(entityType),
    allowDelete: def['allowDelete'] !== undefined ? !!def['allowDelete'] : ['collection', 'external', 'tree', 'dag'].includes(entityType),
    allowDragDrop: ['tree', 'dag'].includes(entityType) ? (def['allowDragDrop'] as boolean | undefined) : undefined,
    hidden: def['hidden'] as boolean | undefined,
    actions: Array.isArray(def['actions']) && def['actions'].length > 0
      ? (def['actions'] as ActionDefinition[]).map(a => toActionDTO(a))
      : undefined,
    realtime: def['realtime'] as EntityDefinitionDTO['realtime'],
    socketKey: moduleName && entityIdent ? `${moduleName}.${entityIdent}` : undefined,
    liveOn: def['liveOn'] as string[] | undefined,
    refreshInterval: def['refreshInterval'] as number | undefined,
    taggeable: def['taggeable'] ? true : undefined,
    attachable: (() => {
      const val = def['attachable']
      if (!val) return undefined
      if (val === true) return true
      const cfg = val as Record<string, unknown>
      return {
        ...(cfg['accept'] ? { accept: cfg['accept'] as string } : {}),
        ...(cfg['maxFiles'] ? { maxFiles: cfg['maxFiles'] as number } : {})
      }
    })(),
    workflow: (() => {
      const wf = def['workflow'] as WorkflowConfig | undefined
      if (!wf) return undefined
      return {
        field: wf.field ?? 'status',
        initial: wf.initial,
        states: wf.states.map(s => ({
          value: s.value,
          label: s.label,
          color: s.color,
          icon: s.icon,
        })),
        transitions: wf.transitions.map(t => ({
          from: t.from,
          to: t.to,
          caslAction: t.casl?.action,
        })),
      }
    })()
  }
}

/**
 * Converts ModuleManifest to a serializable ModuleDTO
 */
export function toModuleDTO(mod: ModuleManifest, ctx: ModuleContext): ModuleDTO {
  const libPath = ctx.core.getLibPath()

  const exposedDefs = (mod.definitions ?? [])
    .filter(def => ('expose' in def ? (def as { expose?: boolean }).expose : true) !== false)

  return {
    name: mod.name,
    label: mod.label!,
    icon: mod.icon,
    description: mod.description,
    type: mod.type ?? 'core',
    category: mod.category,
    dependencies: mod.dependencies ?? [],
    routePrefix: mod.routePrefix ?? `/${mod.name}`,
    subjects: ctx.engine.getModuleSubjects(mod),
    definitions: exposedDefs.map(def =>
      toEntityDefinitionDTO(def as unknown as Record<string, unknown>, ctx.engine, mod.name)
    ),
    definitionsCount: exposedDefs.length,
    actions: (mod.actions ?? []).length > 0
      ? (mod.actions ?? []).filter(a => !a.hidden).map(a => toActionDTO(a))
      : undefined,
    hasRoutes: !!mod.routes || (mod.definitions?.length ?? 0) > 0 || (mod.actions?.length ?? 0) > 0,
    hasMigrate: !!mod.migrate,
    hasSeed: moduleHasSeedWithPath(mod, libPath),
    hasInit: !!mod.init
  }
}

/**
 * Converts PluginManifest to a serializable PluginDTO
 */
export function toPluginDTO(plugin: PluginManifest, ctx: ModuleContext): PluginDTO {
  const state = ctx.core.plugins.getState(plugin.name)
  return {
    name: plugin.name,
    code: plugin.code,
    label: plugin.label,
    icon: plugin.icon,
    category: plugin.category,
    version: plugin.version,
    description: plugin.description,
    installed: true,
    enabled: state?.enabled ?? null,
    modules: plugin.modules.map(mod => toModuleDTO(mod, ctx)),
    envVars: plugin.envVars,
    peerDependencies: plugin.peerDependencies,
    llms: plugin.llms,
    setup: plugin.setup ? {
      steps: plugin.setup.steps,
      docsUrl: plugin.setup.docsUrl,
      prerequisites: plugin.setup.prerequisites,
      caveats: plugin.setup.caveats
    } : undefined
  }
}

